import * as THREE from 'three';
import type { Vec3Spec } from './types';

export const clamp01 = (value: number): number => Math.min(1, Math.max(0, value));

export const smoothstep = (value: number): number => {
  const t = clamp01(value);
  return t * t * (3 - 2 * t);
};

export const smootherstep = (value: number): number => {
  const t = clamp01(value);
  return t * t * t * (t * (t * 6 - 15) + 10);
};

export const ease = (value: number, kind: 'linear' | 'smooth' | 'smoother' = 'smooth'): number => {
  if (kind === 'linear') return clamp01(value);
  return kind === 'smoother' ? smootherstep(value) : smoothstep(value);
};

export const vec3 = (spec: Vec3Spec): THREE.Vector3 => new THREE.Vector3(spec.x, spec.y, spec.z);

/** Small, deterministic PRNG. Never use Math.random in timeline-rendered content. */
export class SeededRandom {
  private state: number;

  constructor(seed: number | string) {
    this.state = typeof seed === 'number' ? seed >>> 0 : hashString(seed);
    if (this.state === 0) this.state = 0x9e3779b9;
  }

  next(): number {
    let x = this.state;
    x ^= x << 13;
    x ^= x >>> 17;
    x ^= x << 5;
    this.state = x >>> 0;
    return this.state / 0xffffffff;
  }

  range(min: number, max: number): number {
    return min + (max - min) * this.next();
  }

  signed(): number {
    return this.next() * 2 - 1;
  }
}

export function hashString(value: string): number {
  let hash = 2166136261;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
}

/** Smooth pseudo-noise made entirely from absolute time. */
export function harmonicNoise(time: number, seed: number, frequency = 1): number {
  const phase = seed * 0.754877666;
  return (
    Math.sin(time * frequency + phase) * 0.55 +
    Math.sin(time * frequency * 2.17 + phase * 1.7) * 0.3 +
    Math.sin(time * frequency * 4.91 + phase * 0.37) * 0.15
  );
}

export function normalizedTime(time: number, start: number, end: number): number {
  if (end <= start) return time >= end ? 1 : 0;
  return clamp01((time - start) / (end - start));
}

export function pulse(time: number, center: number, width: number): number {
  return Math.exp(-Math.pow((time - center) / Math.max(width, 0.0001), 2));
}
